import React from 'react';
import type { IconName } from '@/shared/icons';
import type { VisitsBreakdownRow } from '@troia-v3/types';

import { CHART_COLORS } from '@/shared/constants/charts';
import { DashCard, DeltaBadge, formatNumber } from '@/shared/components/ui/dashboard';

interface Props {
  icon: IconName;
  title: string;
  rows: VisitsBreakdownRow[];
  testIdPrefix: string;
  emptyLabel: string;
}

/**
 * Ranking com barra horizontal. Usado por unidade, canal e origem.
 *
 * Barra e não pizza: a paleta obrigatória tem 4 cores, e categórico com muitas
 * fatias fica ilegível. Com barra, 20 lojas continuam comparáveis.
 */
export const VisitsBreakdownCard: React.FC<Props> = ({ icon, title, rows, testIdPrefix, emptyLabel }) => {
  const max = Math.max(1, ...rows.map((r) => r.views));

  return (
    <DashCard icon={icon} title={title} testId={`${testIdPrefix}-card`}>
      {rows.length === 0 ? (
        <div className="text-sm py-6 text-center" style={{ color: 'var(--theme-muted-text)' }}>
          {emptyLabel}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {rows.map((row) => (
            <div key={row.key} className="flex flex-col gap-1" data-testid={`${testIdPrefix}-row-${row.key}`}>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  {row.label}
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span style={{ color: 'var(--theme-muted-text)' }}>{formatNumber(row.views)}</span>
                  {row.wentSilent ? (
                    // Reportava e sumiu. NÃO mostra delta: -100% aqui é falha de
                    // coleta, e ler isso como queda de movimento é o erro que
                    // este aviso existe para impedir.
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                      style={{
                        backgroundColor: 'var(--theme-warning-subtle)',
                        color: 'var(--theme-warning-fg)',
                      }}
                      title="Reportava no período anterior e parou. Verifique o sensor antes de concluir que o movimento caiu."
                      data-testid={`${testIdPrefix}-silent-${row.key}`}
                    >
                      sem medição
                    </span>
                  ) : (
                    <DeltaBadge deltaPct={row.deltaPct} />
                  )}
                </span>
              </div>
              <div
                className="h-1.5 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--theme-surface-sunken)' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.max(2, (row.views / max) * 100)}%`,
                    backgroundColor: row.wentSilent ? 'var(--theme-card-border)' : CHART_COLORS[1],
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </DashCard>
  );
};

VisitsBreakdownCard.displayName = 'VisitsBreakdownCard';
